import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { determineActiveSection, determineScanline, SectionTop } from './determineActiveSection';

const DEFAULT_NAV_HEIGHT = 52;
const SCROLL_SETTLE_FALLBACK_MS = 250; // Safari has no scrollend event; release once scrolling goes quiet

interface ScrollSpyParams {
  sectionIds: string[];
  stickyTop: number;
  tabNavigationRef: RefObject<HTMLDivElement>;
  enabled?: boolean;
}

export function useScrollSpy({ sectionIds, stickyTop, tabNavigationRef, enabled = true }: ScrollSpyParams) {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const suppressedRef = useRef(false);
  const rafIdRef = useRef(0);
  const releaseTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const releaseSpy = useCallback(() => {
    suppressedRef.current = false;
    clearTimeout(releaseTimeoutRef.current);
    window.removeEventListener('scrollend', releaseSpy);
  }, []);

  const armReleaseFallback = useCallback(() => {
    clearTimeout(releaseTimeoutRef.current);
    releaseTimeoutRef.current = setTimeout(releaseSpy, SCROLL_SETTLE_FALLBACK_MS);
  }, [releaseSpy]);

  useEffect(() => {
    if (!enabled) return undefined;
    const navHeight = () => tabNavigationRef.current?.getBoundingClientRect().height ?? DEFAULT_NAV_HEIGHT;

    const spy = () => {
      if (suppressedRef.current) return;
      const documentHeight = document.documentElement.scrollHeight;
      const scanline = determineScanline({
        scrollY: window.scrollY,
        viewportHeight: window.innerHeight,
        documentHeight,
        stickyOffset: stickyTop + navHeight() + 1,
      });
      const atBottom = window.scrollY + window.innerHeight >= documentHeight - 2;
      const sectionTops = sectionIds
        .map((id): SectionTop | null => {
          const element = document.getElementById(id);
          return element ? { id, top: element.getBoundingClientRect().top + window.scrollY } : null;
        })
        .filter((section): section is SectionTop => section !== null);
      setActiveTab(determineActiveSection(sectionTops, scanline, atBottom));
    };

    const onScroll = () => {
      // While a clicked tab is pinned, never schedule the spy — a frame
      // scheduled before scrollend releases would overwrite the pinned tab.
      if (suppressedRef.current) {
        armReleaseFallback();
        return;
      }
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(spy);
    };

    // land anchor targets below the sticky header + tab bar
    document.documentElement.style.scrollPaddingTop = `${stickyTop + navHeight()}px`;

    spy();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [sectionIds, stickyTop, tabNavigationRef, armReleaseFallback, enabled]);

  // A click pins the target tab; the spy stays muted until the smooth scroll
  // settles and only takes over again on the next user scroll.
  const onTabClick = (sectionId: string) => {
    setActiveTab(sectionId);
    suppressedRef.current = true;
    cancelAnimationFrame(rafIdRef.current);
    window.addEventListener('scrollend', releaseSpy);
    armReleaseFallback();
  };

  return { activeTab, onTabClick };
}
